/* ==========================================================================================
   U-Konserve 2026 · uk-home.js · the home page behaviours (home builder 08-10-2026; looks D1 + D2)
   Loaded deferred by every home section (the second copy only binds new markup). No dependencies.
   [data-ukhm-hero]    the hero slider: slide 1 plays when html gets uk-go (uk-header.js: fonts + decoded
                       img[data-uk-first], max 2.6 s; event 'uk:go'); dots; autoplay (data-speed s) that pauses on
                       hover / focus / hidden tab; swipe; the other slides' photos load after the first load.
   [data-ukhm-reveal]  rise + fade once when it scrolls into view (armed here, so nothing hides without JS).
   [data-ukhm-lps]     Lift. Pack. Seal. (look D2): the section pins; scroll drives --lift / --xf on the stage (the
                       closed box lifts, then crossfades to the open box) and data-step lights the steps + tags.
   prefers-reduced-motion: no autoplay, no scroll motion, everything shown still and complete (CSS does the rest).
   ========================================================================================== */
(() => {
  'use strict';
  const W = window, D = document, H = D.documentElement;
  if (W.UKHome) { W.UKHome.init(D); return; }
  const reduce = W.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
  const each = (root, sel, fn) => { if (root.matches && root.matches(sel)) fn(root); root.querySelectorAll(sel).forEach(fn); };

  /* ---------- the hero slider ---------- */
  const bindHero = (hero) => {
    if (hero.ukBound) return;
    hero.ukBound = 1;
    const slides = [...hero.querySelectorAll('.ukhm-slide')];
    const dots = [...hero.querySelectorAll('.ukhm-dots .ukhm-dot')];
    const speed = Math.max(5, +hero.dataset.speed || 7) * 1000;
    const auto = hero.dataset.autoplay !== 'false' && !reduce && slides.length > 1;
    let cur = Math.max(0, slides.findIndex((s) => s.classList.contains('is-on')));
    let timer = 0, paused = false, started = false;
    if (reduce) { slides.forEach((s) => s.classList.add('is-play')); hero.classList.add('is-still'); }
    const eager = (s) => s.querySelectorAll('img[loading="lazy"]').forEach((im) => { im.loading = 'eager'; });
    const show = (i) => {
      i = (i + slides.length) % slides.length;
      if (i === cur) return;
      const old = slides[cur];
      old.classList.remove('is-on');
      old.setAttribute('aria-hidden', 'true');
      setTimeout(() => { if (!old.classList.contains('is-on')) old.classList.remove('is-play'); }, 950);
      cur = i;
      const s = slides[cur];
      eager(s);
      s.classList.remove('is-play'); void s.offsetWidth;
      s.classList.add('is-on', 'is-play');
      s.removeAttribute('aria-hidden');
      dots.forEach((d, k) => { d.classList.toggle('is-on', k === cur); if (k === cur) d.setAttribute('aria-current', 'true'); else d.removeAttribute('aria-current'); });
    };
    // Tommy 08-10: the active dot fills up over the slide's time, so you can see the next slide coming
    const fill = (ms) => {
      hero.style.setProperty('--ukhm-fill', ms + 'ms');
      dots.forEach((d) => d.classList.remove('is-fill'));
      const a = dots[cur]; if (!a || !auto) return;
      void a.offsetWidth; a.classList.add('is-fill');
    };
    const schedule = (ms) => {
      clearTimeout(timer);
      if (!auto) return;
      fill(ms);
      timer = setTimeout(() => { if (!paused && !D.hidden) show(cur + 1); schedule(speed); }, ms);
    };
    dots.forEach((d, k) => d.addEventListener('click', () => { show(k); schedule(speed + 2000); }));
    const pv = hero.querySelector('[data-ukhm-prev]'), nx = hero.querySelector('[data-ukhm-next]');
    if (pv) pv.addEventListener('click', () => { show(cur - 1); schedule(speed + 2000); });
    if (nx) nx.addEventListener('click', () => { show(cur + 1); schedule(speed + 2000); });
    hero.addEventListener('mouseenter', () => { paused = true; hero.classList.add('is-paused'); });
    hero.addEventListener('mouseleave', () => { paused = false; hero.classList.remove('is-paused'); schedule(Math.round(speed / 2)); });
    hero.addEventListener('focusin', () => { paused = true; });
    hero.addEventListener('focusout', () => { paused = false; });
    let x0 = null;
    hero.addEventListener('pointerdown', (e) => { if (e.pointerType !== 'mouse') x0 = e.clientX; });
    hero.addEventListener('pointerup', (e) => {
      if (x0 === null) return;
      const dx = e.clientX - x0; x0 = null;
      if (Math.abs(dx) > 50) { show(cur + (dx < 0 ? 1 : -1)); schedule(speed + 2000); }
    });
    const start = () => {
      if (started) return;
      started = true;
      slides[cur].classList.add('is-play');
      schedule(speed + 200);
    };
    if (reduce || H.classList.contains('uk-go')) start();
    else {
      D.addEventListener('uk:go', start, { once: true });
      setTimeout(start, 3400);                                  // never wait longer than this
    }
    W.addEventListener('load', () => setTimeout(() => slides.forEach((s, k) => { if (k !== cur) eager(s); }), 2500), { once: true });
    hero.ukShow = (el) => { const n = slides.indexOf(el); if (n >= 0) { show(n); clearTimeout(timer); } };
  };

  /* ---------- reveal on scroll ---------- */
  let io = null;
  const bindReveal = (el) => {
    if (el.ukBound) return;
    el.ukBound = 1;
    if (reduce || !('IntersectionObserver' in W)) { el.classList.add('is-in'); return; }
    if (!io) io = new IntersectionObserver((es) => es.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); } }), { rootMargin: '0px 0px -4% 0px', threshold: 0 });
    const r = el.getBoundingClientRect();
    if (r.top < W.innerHeight && r.bottom > 0) { el.classList.add('is-in'); return; }  // already on screen: no hide
    io.observe(el);
  };

  /* ---------- Lift. Pack. Seal. (look D2: the section pins; scroll lifts the real closed box, then it crossfades to
     the open one; the steps + tags light up in turn) ---------- */
  const bindLps = (sec) => {
    if (sec.ukBound) return;
    sec.ukBound = 1;
    const track = sec.querySelector('.ukhm-lps__track'), stage = sec.querySelector('.ukhm-lps__stage');
    if (!track || !stage || reduce) return;
    const topOf = () => parseFloat(getComputedStyle(sec).getPropertyValue('--ukhm-top')) || 84;
    const render = (p) => {
      const lift = clamp((p - .08) / .26, 0, 1), xf = clamp((p - .3) / .2, 0, 1);
      stage.style.setProperty('--lift', (lift * (1 - xf)).toFixed(3));
      stage.style.setProperty('--xf', xf.toFixed(3));
      const st = p < .42 ? '0' : (p < .7 ? '1' : '2');
      if (sec.dataset.step !== st) sec.dataset.step = st;
    };
    let forced = null;
    const update = () => {
      if (forced !== null) return;
      const r = track.getBoundingClientRect(), top = topOf(), total = r.height - W.innerHeight + top;
      if (r.bottom < -200 || r.top > W.innerHeight + 200) return;
      if (true) {                                                  // never pinned (Tommy 08-10): progress = the section passing by
        render(clamp((W.innerHeight * .8 - r.top) / (r.height * .85), 0, 1));
        return;
      }
      render(total > 0 ? clamp((top - r.top) / total, 0, 1) : 1);
    };
    let ticking = false;
    W.addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(() => { ticking = false; update(); }); } }, { passive: true });
    W.addEventListener('resize', update, { passive: true });
    update();
    W.__ukLps = (p) => { forced = p; render(p); };                // a hook for screenshots / checks
  };
  const api = W.UKHome = {
    init(root) {
      root = root || D;
      if (!reduce) H.classList.add('ukhm-armed');
      each(root, '[data-ukhm-hero]', bindHero);
      each(root, '[data-ukhm-reveal]', bindReveal);
      each(root, '[data-ukhm-lps]', bindLps);
    }
  };
  if (D.readyState === 'loading') D.addEventListener('DOMContentLoaded', () => api.init(D)); else api.init(D);
  D.addEventListener('shopify:section:load', (e) => api.init(e.target));
  D.addEventListener('shopify:block:select', (e) => {
    const hero = e.target.closest && e.target.closest('[data-ukhm-hero]');
    if (hero && hero.ukShow) hero.ukShow(e.target);
  });
})();
